'use client';

import { useTranslations } from 'next-intl';
import { Drawer, Text } from '@cobrastyle/ui';
import { Button } from '@/components/ui/button';
import { useCart } from '@/hooks';
import { useCartFeedback } from '@/hooks/use-cart-feedback';
import { StoreLink } from '@/components/store';
import { useEffect, useRef, useState } from 'react';
import type { Cart } from '@cobrastyle/shared-types';
import { getCartQuantityRules, type CartLineQuantity } from '@/actions/cart-quantity-rules';
import { MiniCartItem } from './mini-cart-item';
import { preserveLineOptions, type LineOptions } from './preserve-line-options';
import { PriceSummary } from './price-summary';
import { CheckoutButton } from './checkout-button';
import { cartTotalExcludingShipping } from '@/lib/cart-total-excluding-shipping';
import { useCartDrawer } from './cart-drawer-provider';

export function CartDrawer() {
  const { isOpen, close } = useCartDrawer();
  const { cart, itemCount, updateItem, removeItem, isLoading } = useCart();
  const { notify } = useCartFeedback();
  const t = useTranslations('cart');
  const tToast = useTranslations('toast');

  // Same option-blink as the cart page — see preserve-line-options.ts.
  const lineOptions = useRef<Map<string, LineOptions>>(new Map());
  const items = preserveLineOptions(cart?.items ?? [], lineOptions.current);
  const quantityRules = useCartQuantityRules(cart);

  // See CartContent: the toast is the only sign of a refused quantity change.
  const handleUpdateQuantity = (itemUid: string, quantity: number) => {
    notify({ error: tToast('updateError') });
    updateItem({ itemUid, quantity });
  };

  const handleRemove = (uid: string) => {
    notify({ success: tToast('itemRemoved'), error: tToast('removeError') });
    removeItem(uid);
  };

  return (
    <Drawer isOpen={isOpen} onClose={close} position="right" title={`${t('title')} (${itemCount})`}>
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 px-6 py-20 text-center">
          <svg className="h-16 w-16 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <Text variant="muted">{t('empty')}</Text>
          <Button onClick={close}>{t('continueShopping')}</Button>
        </div>
      ) : (
        <div className="flex min-h-full flex-col">
          <div className="flex-1 space-y-3 p-4">
            {items.map((item) => (
              <MiniCartItem
                key={item.uid}
                item={item}
                decoration={quantityRules[item.uid]}
                onUpdateQuantity={(quantity) => handleUpdateQuantity(item.uid, quantity)}
                onRemove={() => handleRemove(item.uid)}
                isLoading={isLoading}
              />
            ))}
          </div>

          {/* Sticky footer — pins to the bottom of the Drawer's scroll area. */}
          <div className="sticky bottom-0 space-y-4 border-t border-border bg-card p-4">
            {cart && (
              <PriceSummary
                subtotal={cart.totals.subtotalIncludingTax}
                discounts={cart.totals.discounts}
                tax={cart.totals.taxes.reduce((sum, tx) => sum + tx.amount, 0)}
                taxIncludedInSubtotal
                total={cartTotalExcludingShipping(cart.totals)}
                currency={cart.totals.currency}
              />
            )}
            <CheckoutButton cart={cart} className="w-full" />
            <StoreLink
              href="/varukorg"
              className="block text-center text-sm text-primary transition-colors hover:text-accent-foreground"
            >
              {t('viewCart')}
            </StoreLink>
          </div>
        </div>
      )}
    </Drawer>
  );
}

/**
 * Minimum order and pack size per line, asked of the server after mount — the mini cart has no
 * server render to carry them the way the cart page's decorations do.
 *
 * Asked again only when the set of lines changes: a line's rules follow from what it is, not from
 * how many there are, so a quantity change needs no round trip. Until the answer arrives (or if it
 * fails) a line steps by one, which is what every line did before these rules existed.
 */
function useCartQuantityRules(cart: Cart | null): Record<string, CartLineQuantity> {
  const [rules, setRules] = useState<Record<string, CartLineQuantity>>({});
  const signature = (cart?.items ?? []).map((item) => item.uid).join(',');

  useEffect(() => {
    if (!signature) {
      setRules({});
      return;
    }

    let active = true;
    getCartQuantityRules()
      .then((next) => {
        if (active) setRules(next);
      })
      .catch(() => {
        // Only costs the pack step; the line still renders and Magento still takes the update.
      });

    return () => {
      active = false;
    };
  }, [signature]);

  return rules;
}
